/**
 * AtlasGpuRecoveryModule — a lost WebGL context becomes a reload back to where the player stands (XR P0.3,
 * 2026-09-28). The rules are pure in lib/gpu_recovery.ts; this module only listens and acts.
 *
 * Inert on the WebGPU backend (a WebGPU device loss is the engine's own path) and inert until a context is actually
 * lost, so the desktop game is unchanged in every normal frame. On a loss: end the immersive session if one is
 * presenting, record the resume point (the network module's own record, so the boot re-enters the same world and
 * spot), mark "was in VR", show why, reload — at most three times in ten minutes.
 *
 * On the reloaded page, a Quest-class browser may grant a new immersive session without a click (WebXR navigation,
 * `sessiongranted`); when the mark says the player was in VR, a granted session is taken once the world has loaded,
 * and otherwise the player is told to press Enter VR.
 */

import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { markResume } from '../lib/session_resume.js';
import { markXrResume, noteReload, recoveryPlan, takeXrResume, RELOAD_DELAY_MS, type RecoveryPlan } from '../lib/gpu_recovery.js';

interface XrSessionStarter { requestSession(mode?: 'immersive-vr' | 'immersive-ar' | 'inline'): Promise<void> }

export class AtlasGpuRecoveryModule implements IModule {
  readonly name = 'AtlasGpuRecovery';
  private app!: App;
  private canvas: HTMLCanvasElement | null = null;
  private webgl = false;
  private losses = 0;
  private lastPlan: RecoveryPlan | null = null;
  private xrWanted = false;
  private granted = false;
  private loaded = false;
  private offs: Array<() => void> = [];

  constructor(private readonly xr: XrSessionStarter | null = null) {}

  init(app: App): void {
    this.app = app;
    const r = app.renderer.native as unknown as { domElement?: HTMLCanvasElement; backend?: { isWebGLBackend?: boolean } };
    this.webgl = r.backend?.isWebGLBackend === true;
    this.canvas = r.domElement ?? null;
    if (this.webgl && this.canvas) {
      this.canvas.addEventListener('webglcontextlost', this.onLost);
      this.offs.push(() => this.canvas?.removeEventListener('webglcontextlost', this.onLost));
    }

    // The reloaded page: take a granted immersive session once the world is up.
    this.xrWanted = takeXrResume(Date.now());
    if (this.xrWanted) {
      const xrSys = (navigator as unknown as { xr?: EventTarget }).xr;
      const onGranted = (): void => { this.granted = true; this.tryResumeXr(); };
      xrSys?.addEventListener?.('sessiongranted', onGranted);
      this.offs.push(() => xrSys?.removeEventListener?.('sessiongranted', onGranted));
      this.offs.push(bus.on('atlas:loading-done', () => {
        this.loaded = true;
        this.tryResumeXr();
        if (!this.granted) bus.emit('atlas:toast', { text: 'The graphics device was reset — press Enter VR to go back in.', kind: 'info', ms: 8000 });
      }));
    }

    (globalThis as Record<string, unknown>).__gpuRecovery = () => ({
      backend: this.webgl ? 'webgl2' : 'webgpu',
      losses: this.losses,
      lastPlan: this.lastPlan,
      xrWanted: this.xrWanted,
      granted: this.granted,
      /** Probe lever: lose the context for real (WEBGL_lose_context). The page reloads by the same path. */
      simulate: (): boolean => {
        const gl = (this.app.renderer.native as unknown as { backend?: { gl?: WebGL2RenderingContext } }).backend?.gl;
        const ext = gl?.getExtension('WEBGL_lose_context');
        if (!ext) return false;
        ext.loseContext();
        return true;
      },
    });
  }

  private readonly onLost = (e: Event): void => {
    e.preventDefault(); // the engine's XRModule does too; harmless twice — it keeps the context restorable
    // Nothing may draw against the dead context in the moments before the reload (three reads null state from it).
    try { (this.app.renderer as unknown as { paused: boolean }).paused = true; } catch { /* no renderer */ }
    this.losses++;
    const presenting = atlas.xrPresenting === true;
    const plan = recoveryPlan(presenting, Date.now());
    this.lastPlan = plan;
    console.warn(`[XRRecovery] WebGL context lost (${this.losses}) — ${plan.reload ? 'reloading' : 'staying'}${presenting ? ', ending the VR session' : ''}`);
    if (plan.endSession) {
      try { void (this.app.renderer.native as unknown as { xr?: { getSession?: () => { end(): Promise<void> } | null } }).xr?.getSession?.()?.end(); } catch { /* the session may already be gone */ }
    }
    if (atlas.currentWorldId != null) markResume({ worldId: atlas.currentWorldId, x: atlas.localX, z: atlas.localZ });
    if (plan.markXr) markXrResume(Date.now());
    bus.emit('atlas:toast', { text: plan.message, kind: plan.reload ? 'info' : 'error', ms: plan.reload ? RELOAD_DELAY_MS + 4000 : 20000 });
    if (plan.reload) {
      noteReload(Date.now());
      setTimeout(() => { try { location.reload(); } catch { /* nothing left to do */ } }, RELOAD_DELAY_MS);
    }
  };

  private tryResumeXr(): void {
    if (!this.xrWanted || !this.granted || !this.loaded || !this.xr || atlas.xrPresenting) return;
    this.xrWanted = false;
    this.xr.requestSession('immersive-vr').catch((err: unknown) => {
      console.warn('[XRRecovery] the granted session could not start:', err);
      bus.emit('atlas:toast', { text: 'Press Enter VR to go back in.', kind: 'info', ms: 8000 });
    });
  }

  dispose(): void {
    for (const off of this.offs.splice(0)) { try { off(); } catch { /* ignore */ } }
    delete (globalThis as Record<string, unknown>).__gpuRecovery;
  }
}
